import React from 'react';
import { AttachFile } from '@mui/icons-material';
import { Box, Chip, IconButton, Paper, Stack, Tooltip, Typography } from '@mui/material';
import type { TransactionLogEntry } from './mockTransactionLog';
import { TransactionTypeChip } from './TransactionTypeChip';
import type { TransactionDetailSection } from './TransactionDetailModal';
import { shouldHideDocumentTypeChip, shouldHideTransactionTypeForSource } from './sourceWorkflow';

const resolveDocumentTypeBg = (documentType: TransactionLogEntry['documentType']): string => {
  if (documentType === 'Hand Receipt' || documentType === 'Front Window') {
    return 'primary.main';
  }
  return 'secondary.main';
};

const ORIGIN_TAGS = new Set(['FW', 'PBO', 'KIOSK']);
const CORRECTED_TAG = 'CORRECTED';

const normalizeTags = (tags: TransactionLogEntry['tags']): string[] => {
  if (!Array.isArray(tags) || tags.length === 0) {
    return [];
  }

  const sanitized = tags
    .filter((tag): tag is string => typeof tag === 'string')
    .map((tag) => tag.trim())
    .filter((tag) => tag.length > 0);

  const originIndex = sanitized.findIndex((tag) => ORIGIN_TAGS.has(tag.toUpperCase()));
  if (originIndex === -1) {
    return sanitized;
  }

  const originTag = sanitized[originIndex]?.toUpperCase();
  const otherTags = sanitized
    .filter((_, index) => index !== originIndex)
    .filter((tag) => !ORIGIN_TAGS.has(tag.toUpperCase()));

  return originTag ? [originTag, ...otherTags] : otherTags;
};

export interface TransactionLogCardProps {
  transaction: TransactionLogEntry;
  onOpen: (transactionId: string, options?: { initialSection?: TransactionDetailSection }) => void;
  formatDateTime: (value: string | null | undefined) => string;
}

export const TransactionLogCard: React.FC<TransactionLogCardProps> = ({
  transaction,
  onOpen,
  formatDateTime,
}) => {
  const documentCount = transaction.documentCount ?? 0;

  // Document number is pre-computed server-side; assets/documents are loaded on
  // demand when the detail modal opens.
  const displayDocumentNumber = transaction.documentNumber;

  const tags = normalizeTags(transaction.tags);
  const has4949DocumentTypeChip = (transaction.documentType ?? '').trim().toUpperCase() === '4949';
  const displayTags = tags.filter((tag) => {
    const trimmed = tag.trim();
    if (/^source:/i.test(trimmed)) {
      return false;
    }
    if (/^assets:\d+$/i.test(trimmed) || /^docs:\d+$/i.test(trimmed)) {
      return false;
    }
    if (has4949DocumentTypeChip && trimmed.toUpperCase() === '4949') {
      return false;
    }
    return true;
  });
  const hideTransactionTypeChip = shouldHideTransactionTypeForSource(transaction.sourceWorkflow);
  // Receipt types are pre-computed server-side from asset transaction data.
  // Kiosk (self-service) checkouts suppress PHR/THR badges per requirement.
  const displayReceiptTypes = transaction.isKiosk ? [] : (transaction.handReceiptTypes ?? []);
  const hideDocumentTypeChip =
    hideTransactionTypeChip ||
    shouldHideDocumentTypeChip({
      transactionType: transaction.transactionType,
      documentType: transaction.documentType,
      handReceiptTypes: displayReceiptTypes,
    });

  return (
    <Paper
      variant="outlined"
      onClick={() => onOpen(transaction.id)}
      sx={{
        p: 2,
        borderRadius: 2,
        cursor: 'pointer',
        transition: 'box-shadow 120ms ease, border-color 120ms ease',
        '&:hover': {
          borderColor: 'primary.main',
          boxShadow: 2,
        },
      }}
      role="button"
      tabIndex={0}
      onKeyDown={(event) => {
        if (event.key === 'Enter' || event.key === ' ') {
          event.preventDefault();
          onOpen(transaction.id);
        }
      }}
    >
      <Stack spacing={1}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Typography variant="subtitle1" fontWeight={800}>
              {displayDocumentNumber}
            </Typography>
            {transaction.hasDocuments && (
              <Tooltip title="View documents">
                <IconButton
                  size="small"
                  onClick={(event) => {
                    event.stopPropagation();
                    onOpen(transaction.id, { initialSection: 'documents' });
                  }}
                  aria-label="View documents"
                >
                  <AttachFile fontSize="small" />
                </IconButton>
              </Tooltip>
            )}
          </Box>
          <Typography variant="body2" color="text.secondary">
            {formatDateTime(transaction.dateProcessed)}
          </Typography>
        </Box>

        {transaction.logFormNumber ? (
          <Typography variant="body2">Log #{transaction.logFormNumber}</Typography>
        ) : (
          <Typography variant="body2">{transaction.memberName}</Typography>
        )}

        <Stack direction="row" spacing={1} useFlexGap flexWrap="wrap" alignItems="center">
          {!hideTransactionTypeChip && (
            <TransactionTypeChip
              value={transaction.transactionType}
              cancellationScope={transaction.cancellationScope}
            />
          )}
          {transaction.documentType && !hideDocumentTypeChip && (
            <Chip
              size="small"
              label={transaction.documentType}
              sx={{
                bgcolor: resolveDocumentTypeBg(transaction.documentType),
                color: 'common.white',
                fontWeight: 700,
                '& .MuiChip-label': { px: 1.25 },
              }}
            />
          )}
          {displayReceiptTypes.map((type) => (
            <Chip
              key={type}
              size="small"
              label={type}
              variant="outlined"
              sx={{
                fontWeight: 800,
                letterSpacing: '0.05em',
                bgcolor: (t) =>
                  type === 'PHR'
                    ? t.palette.mode === 'dark'
                      ? 'rgba(63, 127, 85, 0.18)'
                      : 'success.50'
                    : t.palette.mode === 'dark'
                      ? 'rgba(193, 125, 31, 0.18)'
                      : 'warning.50',
                borderColor: type === 'PHR' ? 'success.main' : 'warning.main',
                color: type === 'PHR' ? 'success.light' : 'warning.light',
                borderWidth: 1.5,
                '& .MuiChip-label': { px: 1.25 },
              }}
            />
          ))}

          {displayTags.map((tag, index) => {
            const isOriginTag = index === 0 && ORIGIN_TAGS.has(tag.toUpperCase());
            return (
              <Chip
                key={`${tag}-${index}`}
                size="small"
                label={tag}
                variant={isOriginTag || tag.toUpperCase() === CORRECTED_TAG ? 'filled' : 'outlined'}
                sx={
                  isOriginTag
                    ? {
                        bgcolor: (t) => (t.palette.mode === 'dark' ? 'grey.700' : 'grey.900'),
                        color: 'common.white',
                        fontWeight: 800,
                        letterSpacing: '0.08em',
                        '& .MuiChip-label': { px: 1.25 },
                      }
                    : tag.toUpperCase() === CORRECTED_TAG
                      ? {
                          bgcolor: 'error.main',
                          color: 'common.white',
                          fontWeight: 800,
                          letterSpacing: '0.08em',
                          '& .MuiChip-label': { px: 1.25 },
                        }
                      : {
                          fontWeight: 700,
                          bgcolor: (t) => (t.palette.mode === 'dark' ? 'grey.800' : 'grey.50'),
                          borderColor: (t) => (t.palette.mode === 'dark' ? 'grey.600' : 'grey.300'),
                          color: 'text.secondary',
                        }
                }
              />
            );
          })}
          <Chip
            size="small"
            clickable
            variant="outlined"
            label={`${transaction.totalAssets} assets`}
            onClick={(event) => {
              event.stopPropagation();
              onOpen(transaction.id, { initialSection: 'assets' });
            }}
            sx={{
              fontWeight: 700,
              bgcolor: (t) => (t.palette.mode === 'dark' ? 'grey.800' : 'grey.50'),
              borderColor: (t) => (t.palette.mode === 'dark' ? 'grey.600' : 'grey.300'),
            }}
          />
          <Chip
            size="small"
            clickable={documentCount > 0}
            variant="outlined"
            label={`${documentCount} docs`}
            disabled={documentCount === 0}
            onClick={(event) => {
              event.stopPropagation();
              onOpen(transaction.id, { initialSection: 'documents' });
            }}
            sx={{
              fontWeight: 700,
              bgcolor: (t) => (t.palette.mode === 'dark' ? 'grey.800' : 'grey.50'),
              borderColor: (t) => (t.palette.mode === 'dark' ? 'grey.600' : 'grey.300'),
            }}
          />
          {transaction.hasBuyProgramItems && transaction.buyProgramTotal != null && (
            <Chip
              size="small"
              variant="outlined"
              label={`$${transaction.buyProgramTotal.toFixed(2)}`}
              sx={{
                fontWeight: 700,
                bgcolor: (t) =>
                  t.palette.mode === 'dark' ? 'rgba(63, 127, 85, 0.18)' : 'success.50',
                borderColor: 'success.main',
                color: 'success.light',
              }}
            />
          )}
          {transaction.isSocBuyProgram && (
            <Chip
              size="small"
              label="SOC"
              sx={{
                fontWeight: 800,
                letterSpacing: '0.05em',
                bgcolor: 'info.main',
                color: 'common.white',
                '& .MuiChip-label': { px: 1.25 },
              }}
            />
          )}
        </Stack>
      </Stack>
    </Paper>
  );
};
